import axios from 'axios';

const api = {
  async listKits(params = {}) {
    const { data } = await axios.get('/api/kits', { params });
    return data || { rows: [], total: 0 };
  },

  async fetchKit(kitId) {
    if (!kitId) return null;
    const { data } = await axios.get(`/api/kits/${encodeURIComponent(kitId)}`);
    return data;
  },

  async fetchKitHistory(kitId) {
    if (!kitId) return [];
    const { data } = await axios.get(`/api/kits/${encodeURIComponent(kitId)}/history`);
    return Array.isArray(data) ? data : [];
  },

  async applyKitHistory(kitId, auditId, direction = 'before') {
    if (!kitId || !auditId) return null;
    const url = `/api/kits/${encodeURIComponent(kitId)}/history/${auditId}/apply`;
    const { data } = await axios.post(url, { direction });
    return data;
  },

  async fetchKitUsage(kitId) {
    if (!kitId) return [];
    const { data } = await axios.get(`/api/kits/${encodeURIComponent(kitId)}/usage`);
    return Array.isArray(data) ? data : [];
  },

  async fetchReuseSuggestions(components, options) {
    const { data } = await axios.post('/api/kits/reuse-suggestions', { components, options });
    return Array.isArray(data) ? data : [];
  },

  async createKit(payload) {
    const { data } = await axios.post('/api/kits', payload);
    return data;
  },

  async updateKit(kitId, payload) {
    const { data } = await axios.patch(`/api/kits/${encodeURIComponent(kitId)}`, payload);
    return data;
  },

  async searchComponents(query) {
    const payload = typeof query === 'string' ? { query } : (query || {});
    const { data } = await axios.post('/api/kits/component-search', payload);
    return data;
  },

  async deleteKit(kitId) {
    if (!kitId) return null;
    const { data } = await axios.delete(`/api/kits/${encodeURIComponent(kitId)}`);
    return data;
  },

  async listKitItemTypes() {
    const { data } = await axios.get('/api/kit-item-types');
    return Array.isArray(data) ? data : [];
  },

  async createKitItemType(body) {
    const { data } = await axios.post('/api/kit-item-types', body);
    return data;
  },

  async listCoreItemTypes() {
    const { data } = await axios.get('/api/item-types');
    return Array.isArray(data) ? data : [];
  },

  async listFitmentLocationsForItemType(itemTypeKey) {
    if (!itemTypeKey) return [];
    const { data } = await axios.get(`/api/item-types/${itemTypeKey}/fitment-locations`);
    return Array.isArray(data) ? data : [];
  },

  async listKitFitmentLocations() {
    const { data } = await axios.get('/api/kit-fitment-locations');
    return Array.isArray(data) ? data : [];
  },

  async createKitFitmentLocation(body) {
    const { data } = await axios.post('/api/kit-fitment-locations', body);
    return data;
  },

  async applyKitToFitment(fitmentKey, body) {
    const { data } = await axios.post(`/api/fitments/${fitmentKey}/kit`, body);
    return data;
  },

  async removeKitFromFitment(fitmentKey) {
    const { data } = await axios.delete(`/api/fitments/${fitmentKey}/kit`);
    return data;
  },

  // ─── Kit Import API ──────────────────────────────────────────────────────
  async downloadImportTemplate() {
    const response = await axios.get('/api/kits/import-template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'kit-import-template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  async parseImportFile(file) {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await axios.post('/api/kits/import-parse', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  },

  async previewImport(kitGroups) {
    const { data } = await axios.post('/api/kits/import-preview', { kitGroups });
    return data;
  },

  async refreshPreview(kitIds) {
    const { data } = await axios.post('/api/kits/refresh-preview', { kitIds });
    return data;
  },

  async executeImport(kitGroups) {
    const { data } = await axios.post('/api/kits/import-execute', { kitGroups });
    return data;
  },

  async resolveSkuForImport(payload) {
    const { data } = await axios.post('/api/kits/import-resolve-sku', payload);
    return data;
  },

  async unlinkAllFitments(kitId) {
    const { data } = await axios.delete(`/api/kits/${encodeURIComponent(kitId)}/unlink-all`);
    return data;
  }
};

export default api;
